import {
  BoxGeometry,
  Color,
  ConeGeometry,
  CylinderGeometry,
  DynamicDrawUsage,
  IcosahedronGeometry,
  InstancedMesh,
  Matrix4,
  MeshStandardMaterial,
  Quaternion,
  Vector3,
  type BufferGeometry,
  type Scene,
} from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import type { City, Ornament, OrnamentKind } from '@/world/city';
import { PALETTE, AI } from '@/config';
import { clamp01, easeOutCubic } from '@/utils/math';

/**
 * Renders the city's set-dressing — the small punctuation marks the evolution
 * engine plants so each preference axis reads as a distinct visual signature:
 * lanterns for intimacy/routes, gates for velocity thresholds, gardens (trees /
 * stones / pools) for contemplation, beacons for verticality and the finale.
 *
 * Structurally this is a sibling of {@link SkyLinkField}: one InstancedMesh per
 * kind (six draw calls total), a small procedural geometry merged from primitive
 * parts, and a per-instance scale-in "growth" so ornaments *arrive* rather than
 * blink into being — the same "the city is building this for you" language as the
 * rest of the world.
 *
 * Because the model layer (`city.ts`) is owned elsewhere, an ornament has no
 * `dying` flag. Removal is handled two ways, both self-contained here:
 *   1. {@link removeNear} marks ornaments and shrinks them out gracefully.
 *   2. Any ornament spliced straight out of `city.ornaments` by another system is
 *      detected by slot-ownership reconciliation and its slot reclaimed.
 */

const _m = new Matrix4();
const _pos = new Vector3();
const _quat = new Quaternion();
const _scale = new Vector3();
const _up = new Vector3(0, 1, 0);

const KINDS: OrnamentKind[] = ['lantern', 'gate', 'tree', 'stone', 'beacon', 'pool'];
/** Kinds that read as solid mass and should drop shadows. The luminous / flat
 *  kinds (lantern, beacon, pool) skip shadow casting — cheaper and truer to the
 *  ink-wash restraint (light doesn't cast hard shadows here). */
const SHADOW_KINDS = new Set<OrnamentKind>(['gate', 'tree', 'stone']);

interface KindMesh {
  mesh: InstancedMesh;
  free: number[];
  /** ornament id occupying each slot, or -1 — used to reclaim orphaned slots. */
  owner: Int32Array;
}

export class OrnamentField {
  private fields = new Map<OrnamentKind, KindMesh>();
  private dying = new Set<number>();
  /** Reused across frames so removal reconciliation allocates nothing. */
  private aliveIds = new Set<number>();
  private readonly capacity: number;
  /** Finale memory: fraction (0..1) by which every ornament has dissolved. */
  private targetMemory = 0;
  private appliedMemory = 0;

  constructor(scene: Scene, capacity = 512) {
    this.capacity = capacity;
    for (const kind of KINDS) {
      const { geo, mat } = buildKind(kind);
      const mesh = new InstancedMesh(geo, mat, capacity);
      mesh.instanceMatrix.setUsage(DynamicDrawUsage);
      mesh.castShadow = SHADOW_KINDS.has(kind);
      mesh.receiveShadow = false;
      mesh.frustumCulled = false; // one object spanning the whole city
      mesh.count = capacity;

      const free: number[] = [];
      const owner = new Int32Array(capacity).fill(-1);
      for (let i = capacity - 1; i >= 0; i--) {
        _m.makeScale(0, 0, 0); // start hidden
        mesh.setMatrixAt(i, _m);
        free.push(i);
      }
      mesh.instanceMatrix.needsUpdate = true;
      scene.add(mesh);
      this.fields.set(kind, { mesh, free, owner });
    }
  }

  /**
   * Mark every ornament within `radius` of (x,z) to shrink away — the graceful
   * counterpart used when the city decays. They fade over the next few frames and
   * are then spliced out of the model.
   */
  removeNear(x: number, z: number, radius: number, city: City): void {
    const r2 = radius * radius;
    for (const orn of city.ornaments) {
      const dx = orn.x - x;
      const dz = orn.z - z;
      if (dx * dx + dz * dz <= r2) this.dying.add(orn.id);
    }
  }

  private writeMatrix(km: KindMesh, orn: Ornament, elapsed: number): void {
    if (orn.slot < 0) return;
    // Finale dissolve: uniform scale toward each ornament's own centre as memory
    // rises. Render-side only — orn.growth is never touched, so m→0 restores exactly.
    const memF = Math.max(1e-4, 1 - this.appliedMemory);
    const s = Math.max(1e-4, easeOutCubic(orn.growth) * orn.scale * memF);
    let y = orn.y;
    // Lanterns hang in the air; a very gentle bob keeps them feeling paper-light.
    if (orn.kind === 'lantern') y += Math.sin(elapsed * 1.3 + orn.seed) * 0.12 * orn.growth;
    _pos.set(orn.x, y, orn.z);
    _quat.setFromAxisAngle(_up, orn.yaw);
    _scale.set(s, s, s);
    _m.compose(_pos, _quat, _scale);
    km.mesh.setMatrixAt(orn.slot, _m);
  }

  private freeSlot(km: KindMesh, slot: number): void {
    _m.makeScale(0, 0, 0);
    km.mesh.setMatrixAt(slot, _m);
    km.owner[slot] = -1;
    km.free.push(slot);
  }

  update(city: City, dt: number, elapsed: number): void {
    // Grow a touch faster than buildings so a change reads promptly (spec: ~0.6x).
    const rate = dt / (AI.growthDuration * 0.6);
    const dirty = new Set<OrnamentKind>();

    // 1) Reclaim slots whose ornament was removed from the model directly (e.g. the
    //    evolution engine dissolving ornaments during decay). Cheap: caps*kinds.
    this.aliveIds.clear();
    for (const orn of city.ornaments) this.aliveIds.add(orn.id);
    for (const [kind, km] of this.fields) {
      for (let slot = 0; slot < this.capacity; slot++) {
        const id = km.owner[slot];
        if (id >= 0 && !this.aliveIds.has(id)) {
          this.freeSlot(km, slot);
          this.dying.delete(id);
          dirty.add(kind);
        }
      }
    }

    // 2) Assign slots, advance growth, and shrink out dying ornaments.
    for (let i = city.ornaments.length - 1; i >= 0; i--) {
      const orn = city.ornaments[i];
      const km = this.fields.get(orn.kind);
      if (!km) continue;
      const isDying = this.dying.has(orn.id);

      if (orn.slot < 0) {
        if (isDying) {
          // Died before it was ever shown — just forget it.
          city.ornaments.splice(i, 1);
          this.dying.delete(orn.id);
          continue;
        }
        const slot = km.free.pop();
        if (slot === undefined) continue; // capacity exhausted: stay invisible
        orn.slot = slot;
        km.owner[slot] = orn.id;
      }

      if (isDying) {
        orn.growth -= rate * 1.5;
        if (orn.growth <= 0) {
          this.freeSlot(km, orn.slot);
          orn.slot = -1;
          city.ornaments.splice(i, 1);
          this.dying.delete(orn.id);
        } else {
          this.writeMatrix(km, orn, elapsed);
        }
        dirty.add(orn.kind);
      } else if (orn.growth < 1) {
        orn.growth = clamp01(orn.growth + rate);
        this.writeMatrix(km, orn, elapsed);
        dirty.add(orn.kind);
      } else if (orn.kind === 'lantern') {
        // Settled lanterns still need re-writing each frame for the bob.
        this.writeMatrix(km, orn, elapsed);
        dirty.add('lantern');
      }
    }

    // 3) Finale memory: on frames where it moves, rewrite every live ornament so the
    //    whole set-dressing layer dissolves (m→1) or returns (m→0) together.
    if (Math.abs(this.targetMemory - this.appliedMemory) > 0.003) {
      this.appliedMemory = this.targetMemory;
      for (const orn of city.ornaments) {
        if (orn.slot < 0) continue;
        const km = this.fields.get(orn.kind);
        if (!km) continue;
        this.writeMatrix(km, orn, elapsed);
        dirty.add(orn.kind);
      }
    }

    for (const kind of dirty) {
      const km = this.fields.get(kind);
      if (km) km.mesh.instanceMatrix.needsUpdate = true;
    }
  }

  /**
   * Finale hook: dissolve (m→1) or restore (m→0) every ornament. Cheap — a full
   * rewrite happens only on frames where the memory value actually changes.
   */
  setMemory(m: number): void {
    this.targetMemory = clamp01(m);
  }

  dispose(): void {
    for (const km of this.fields.values()) {
      km.mesh.geometry.dispose();
      (km.mesh.material as MeshStandardMaterial).dispose();
      km.mesh.removeFromParent();
    }
    this.fields.clear();
  }
}

/* ------------------------------------------------------------------ */
/* Procedural geometry — each kind merged into one BufferGeometry.     */
/* Ground-sitting kinds anchor their base at local y=0 so growth rises */
/* from the floor; floating kinds (lantern, pool) centre on their y.   */
/* ------------------------------------------------------------------ */

function buildKind(kind: OrnamentKind): { geo: BufferGeometry; mat: MeshStandardMaterial } {
  switch (kind) {
    case 'lantern': {
      // A small rounded volume that hangs and glows warm.
      const geo = new BoxGeometry(0.5, 0.72, 0.5);
      const mat = new MeshStandardMaterial({
        color: new Color(PALETTE.lantern).multiplyScalar(0.85),
        emissive: new Color(PALETTE.lantern),
        emissiveIntensity: 0.95,
        roughness: 0.6,
        metalness: 0.0,
      });
      return { geo, mat };
    }
    case 'gate': {
      // Torii silhouette: two square pillars, a lintel, and a wider top rail.
      const left = new BoxGeometry(0.24, 3.0, 0.24).translate(-1.1, 1.5, 0);
      const right = new BoxGeometry(0.24, 3.0, 0.24).translate(1.1, 1.5, 0);
      const lintel = new BoxGeometry(2.9, 0.3, 0.4).translate(0, 3.0, 0);
      const rail = new BoxGeometry(3.35, 0.26, 0.34).translate(0, 3.42, 0);
      const geo = mergeParts([left, right, lintel, rail]);
      const mat = new MeshStandardMaterial({
        color: new Color(PALETTE.accent).lerp(new Color(PALETTE.building), 0.28),
        roughness: 0.85,
        metalness: 0.0,
      });
      return { geo, mat };
    }
    case 'tree': {
      // Short trunk under two stacked soft cones — a garden pine in ink-green.
      const trunk = new CylinderGeometry(0.12, 0.17, 0.9, 6).translate(0, 0.45, 0);
      const lower = new ConeGeometry(0.75, 1.3, 8).translate(0, 1.35, 0);
      const upper = new ConeGeometry(0.5, 1.0, 8).translate(0, 2.15, 0);
      const geo = mergeParts([trunk, lower, upper]);
      const mat = new MeshStandardMaterial({
        color: new Color(PALETTE.pine),
        roughness: 0.9,
        metalness: 0.0,
      });
      return { geo, mat };
    }
    case 'stone': {
      // A low, rounded boulder: a coarse icosahedron squashed flat.
      const geo = new IcosahedronGeometry(0.55, 0);
      geo.scale(1.7, 0.75, 1.25);
      geo.translate(0, 0.55 * 0.75, 0); // rest its base on the ground
      const mat = new MeshStandardMaterial({
        color: new Color(PALETTE.stone),
        roughness: 0.95,
        metalness: 0.0,
      });
      return { geo, mat };
    }
    case 'beacon': {
      // A slender glowing rod with a bright tip so it reads from far away.
      const rod = new CylinderGeometry(0.09, 0.13, 4.0, 6).translate(0, 2.0, 0);
      const tip = new IcosahedronGeometry(0.22, 0).translate(0, 4.0, 0);
      const geo = mergeParts([rod, tip]);
      const mat = new MeshStandardMaterial({
        color: new Color(PALETTE.lantern).multiplyScalar(0.9),
        emissive: new Color(PALETTE.lantern),
        emissiveIntensity: 1.6,
        roughness: 0.5,
        metalness: 0.0,
      });
      return { geo, mat };
    }
    case 'pool': {
      // A flat celadon disc; a faint emissive keeps it reading as still water.
      const geo = new CylinderGeometry(1.4, 1.4, 0.08, 20).translate(0, 0.04, 0);
      const mat = new MeshStandardMaterial({
        color: new Color(PALETTE.water),
        emissive: new Color(PALETTE.water),
        emissiveIntensity: 0.14,
        roughness: 0.3,
        metalness: 0.0,
      });
      return { geo, mat };
    }
  }
}

/** Merge primitive parts into one geometry and dispose the parts. */
function mergeParts(parts: BufferGeometry[]): BufferGeometry {
  // Primitive geometries disagree about indexing (Icosahedron is non-indexed,
  // Box/Cylinder/Cone are indexed) and mergeGeometries refuses to mix the two —
  // normalize everything to non-indexed first. The vertex-count cost is trivial
  // at these sizes.
  const flat = parts.map((p) => {
    const ni = p.index ? p.toNonIndexed() : p;
    if (ni !== p) p.dispose();
    return ni;
  });
  const merged = mergeGeometries(flat, false);
  for (const p of flat) {
    if (p !== merged) p.dispose();
  }
  return merged ?? flat[0];
}
